import type { Layout, QuestionChoice } from '@mentimeter/http-clients';
import {
  NonInteractiveVisualizationWrapper,
  TitleSection,
} from '@mentimeter/question-modules-shared';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import type { QuizResult } from '@mentimeter/quiz-types';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { QuizBarsDataHandler } from './QuizBarsDataHandler';

export const ViewOnlyPresentation = ({
  question,
  questionStyled,
  id,
  meta,
  description,
  choices,
  layout,
  shouldAnimate,
  results,
  isShowingPreviewResults,
  CanvasVisualizationArea,
}: {
  question: string;
  questionStyled: Record<string, unknown>;
  meta: string;
  description: string;
  id: string;
  choices: QuestionChoice[];
  layout: Layout | undefined;
  shouldAnimate: boolean;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  results: QuizResult[];
  isShowingPreviewResults: boolean;
}) => {
  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  if (isShowingPreviewResults) {
    return (
      <>
        <TitleSection
          title={question}
          styledTitle={questionStyled}
          label={meta}
          description={description}
          fontSizeOffset={layout?.font_size_offset}
          showTextSizeDropdown={false}
        />
        <CenteringContainer mt={4}>
          <QuizBarsDataHandler
            showCorrect={results.length > 0}
            rawResults={results}
            choices={choices}
            enableAnimations={shouldAnimate}
            // Force re-render when toggling preview results
            // to avoid reusing the same visualization instance, as it's not really designed to be updated after initial render
            key={`${id}-preview`}
          />
        </CenteringContainer>
      </>
    );
  }

  return (
    <>
      <TitleSection
        title={question}
        styledTitle={questionStyled}
        label={meta}
        fontSizeOffset={layout?.font_size_offset}
        description={description}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper width="100%" mt={4} flex="1">
        <CenteringContainer>
          <QuizBarsDataHandler
            showCorrect={results.length > 0}
            rawResults={results}
            choices={choices}
            enableAnimations={shouldAnimate}
            key={id}
          />
        </CenteringContainer>
      </ConditionalWrapper>
    </>
  );
};
